import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { QuoteTemplate, PlaceholderPosition, PaymentCondition, PageBackground } from '@/types/quote';
import { toast } from 'sonner';

// Helper to convert Supabase Json to our types
const parseTemplate = (dbTemplate: any): QuoteTemplate => {
  return {
    ...dbTemplate,
    placeholder_positions: Array.isArray(dbTemplate.placeholder_positions) 
      ? dbTemplate.placeholder_positions as PlaceholderPosition[]
      : (dbTemplate.placeholder_positions ? JSON.parse(dbTemplate.placeholder_positions as string) : null),
    default_payment_conditions: Array.isArray(dbTemplate.default_payment_conditions)
      ? dbTemplate.default_payment_conditions
      : (dbTemplate.default_payment_conditions ? JSON.parse(dbTemplate.default_payment_conditions as string) : null),
    page_backgrounds: Array.isArray(dbTemplate.page_backgrounds)
      ? dbTemplate.page_backgrounds as PageBackground[]
      : (dbTemplate.page_backgrounds ? JSON.parse(dbTemplate.page_backgrounds as string) : null),
    page_count: dbTemplate.page_count || 1,
  };
};

export const useQuoteTemplates = () => {
  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('quote_templates')
        .select('*')
        .is('deleted_at', null)
        .order('name');

      if (error) throw error;
      setTemplates((data || []).map(parseTemplate));
    } catch (error: any) {
      console.error('Error fetching templates:', error);
      toast.error('Fout bij laden templates', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async (data: Partial<QuoteTemplate>) => {
    try {
      if (!data.name) {
        throw new Error('Naam is verplicht');
      }

      // If is_default is true, reset other templates
      if (data.is_default) {
        await supabase
          .from('quote_templates')
          .update({ is_default: false })
          .eq('is_default', true);
      }

      const user = await supabase.auth.getUser();
      
      const { data: newTemplate, error } = await supabase
        .from('quote_templates')
        .insert([{
          name: data.name,
          description: data.description || null,
          is_default: data.is_default || false,
          template_body: data.template_body || null,
          template_terms: data.template_terms || null,
          header_logo_url: data.header_logo_url || null,
          background_image_url: data.background_image_url || null,
          brand_primary: data.brand_primary || '#014336',
          brand_secondary: data.brand_secondary || '#10B981',
          footer_text: data.footer_text || null,
          pdf_template_url: data.pdf_template_url || null,
          pdf_page_count: data.pdf_page_count || null,
          placeholder_positions: data.placeholder_positions ? JSON.stringify(data.placeholder_positions) : null,
          default_project_goal: data.default_project_goal || null,
          default_work_description: data.default_work_description || null,
          default_advice_conclusion: data.default_advice_conclusion || null,
          default_disclaimer: data.default_disclaimer || null,
          default_payment_conditions: data.default_payment_conditions ? JSON.stringify(data.default_payment_conditions) : null,
          terms_document_url: data.terms_document_url || null,
          page_count: data.page_count || 1,
          page_backgrounds: data.page_backgrounds ? JSON.stringify(data.page_backgrounds) : null,
          created_by: user.data.user?.id
        }])
        .select()
        .single();

      if (error) throw error;

      setTemplates(prev => [...prev, parseTemplate(newTemplate)]);
      toast.success('Template aangemaakt');
      return newTemplate;
    } catch (error: any) {
      console.error('Error creating template:', error);
      toast.error('Fout bij aanmaken template', {
        description: error.message
      });
      throw error;
    }
  };

  const updateTemplate = async (id: string, data: Partial<QuoteTemplate>) => {
    try {
      // If is_default is true, reset other templates
      if (data.is_default) {
        await supabase
          .from('quote_templates')
          .update({ is_default: false })
          .eq('is_default', true)
          .neq('id', id);
      }

      // Prepare update data with proper JSON serialization
      const updateData: any = { ...data };
      if (updateData.placeholder_positions) {
        updateData.placeholder_positions = JSON.stringify(updateData.placeholder_positions);
      }
      if (updateData.default_payment_conditions) {
        updateData.default_payment_conditions = JSON.stringify(updateData.default_payment_conditions);
      }
      if (updateData.page_backgrounds) {
        updateData.page_backgrounds = JSON.stringify(updateData.page_backgrounds);
      }

      const { data: updatedTemplate, error } = await supabase
        .from('quote_templates')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setTemplates(prev => prev.map(t => t.id === id ? parseTemplate(updatedTemplate) : t));
      toast.success('Template bijgewerkt');
      return updatedTemplate;
    } catch (error: any) {
      console.error('Error updating template:', error);
      toast.error('Fout bij bijwerken template', {
        description: error.message
      });
      throw error;
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('quote_templates')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template verwijderd');
    } catch (error: any) {
      console.error('Error deleting template:', error);
      toast.error('Fout bij verwijderen template', {
        description: error.message
      });
      throw error;
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  return {
    templates,
    loading,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate
  };
};
